/**
 * Persistent footer bar across the Build hub.
 *
 * Mirrors the prototype's `.golive` element — a fixed-position bar with
 * a neutral status note on the left and two actions on the right.
 *
 * Phase 2 wiring:
 *   - "Test it" → switches the hub to the Test & preview tab (active today).
 *   - "Forward my line & go live" → disabled with a "Coming next" tooltip
 *     because Activation (Stripe Checkout + phone-number provisioning)
 *     is Phase 3.
 */

import { Icon } from "./Icon.js";

interface GoLiveBarProps {
  /** Switch the hub to the Test & preview tab. */
  onTest: () => void;
  /** Free-text note shown on the left, defaulted by the parent based on KB state. */
  note?: string;
}

/**
 * Sticky footer for the Build hub. The "Forward my line & go live" CTA
 * is intentionally disabled in Phase 2 — wiring it to real activation
 * lands in Phase 3 (Stripe Checkout + Twilio number provisioning).
 */
export function GoLiveBar({
  onTest,
  note = "Looking good. We'll let you know when phone forwarding is ready.",
}: GoLiveBarProps) {
  return (
    <div className="golive" data-testid="golive-bar">
      <div className="gl-inner">
        <div className="gl-note">
          <Icon name="info" size={16} color="var(--ink-3)" />
          {note}
        </div>
        <div className="gl-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={onTest}
            data-testid="golive-test"
          >
            <Icon name="play-circle" size={16} />
            <span style={{ marginLeft: 6 }}>Test it</span>
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled
            aria-disabled="true"
            title="Coming next — phone forwarding lands in the next update."
            data-testid="golive-activate"
          >
            <Icon name="zap" size={16} />
            <span style={{ marginLeft: 6 }}>Forward my line &amp; go live</span>
          </button>
        </div>
      </div>
    </div>
  );
}
